import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Star, Flag, ArrowUpRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../../context/AuthContext";

export const STATUSES = ["NEW", "REVIEWED", "SHORTLISTED", "SELECTED", "PASSED"];

export const statusCls = (s) =>
  ({
    NEW: "text-secondary border-secondary/40",
    REVIEWED: "text-foreground border-white/25",
    SHORTLISTED: "text-primary border-primary/50",
    SELECTED: "text-green-400 border-green-400/40",
    PASSED: "text-muted-foreground border-white/10",
  }[s] || "text-muted-foreground border-white/10");

export const LANG_LABELS = { en: "EN", es: "ES", fr: "FR", pt: "PT" };

export const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const selectCls =
  "bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-xs uppercase tracking-[0.1em] text-foreground focus:outline-none focus:border-primary/60 [&>option]:bg-[#0a0a0a]";

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [flag, setFlag] = useState("");
  const [language, setLanguage] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status) params.set("status", status);
      if (flag) params.set("flag", flag);
      if (language) params.set("language", language);
      const data = await apiFetch(`/admin/applications?${params.toString()}`);
      setApps(data);
    } catch (e) {
      /* handled by guard */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, flag, language]);

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-secondary mb-2">
            Canarts · Artist Discovery
          </p>
          <h1 className="font-display font-bold tracking-tighter text-3xl md:text-4xl">
            APPLICATIONS
          </h1>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="flex items-center gap-2 w-full md:w-auto"
        >
          <div className="relative flex-1 md:w-72">
            <Search
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              data-testid="admin-apps-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, city..."
              className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-primary/60 transition-colors"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-apps-search-btn"
            className="bg-primary text-white rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:brightness-110 transition-[filter]"
          >
            Search
          </button>
        </form>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          data-testid="admin-apps-filter-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={selectCls}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-apps-filter-flag"
          value={flag}
          onChange={(e) => setFlag(e.target.value)}
          className={selectCls}
        >
          <option value="">All flags</option>
          <option value="high_potential">High potential</option>
          <option value="follow_up">Follow up</option>
        </select>
        <select
          data-testid="admin-apps-filter-language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className={selectCls}
        >
          <option value="">All languages</option>
          {Object.entries(LANG_LABELS).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] animate-pulse py-16 text-center">
          Loading applications
        </p>
      ) : apps.length === 0 ? (
        <p data-testid="admin-apps-empty" className="text-sm text-muted-foreground py-16 text-center">
          No applications found.
        </p>
      ) : (
        <div className="space-y-2">
          {apps.map((a) => (
            <div
              key={a.id}
              role="button"
              tabIndex={0}
              data-testid={`admin-app-row-${a.id}`}
              onClick={() => navigate(`/admin/applications/${a.id}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/admin/applications/${a.id}`)}
              className="w-full text-left group flex flex-col md:flex-row md:items-center gap-3 md:gap-6 border border-white/10 rounded-xl px-5 py-4 hover:border-primary/40 hover:bg-white/[0.03] transition-colors duration-300 cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <p className="font-display font-bold text-lg truncate">
                    {a.stage_name}
                  </p>
                  {a.high_potential && (
                    <Star size={14} className="text-primary fill-primary shrink-0" />
                  )}
                  {a.follow_up && (
                    <Flag size={14} className="text-secondary shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {a.full_name} · {a.email} · {a.city}
                </p>
              </div>
              <div className="flex items-center gap-3 md:gap-5 shrink-0 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {a.artist_type}
                </span>
                <span className="text-[11px] uppercase tracking-[0.12em] text-secondary">
                  {LANG_LABELS[a.language] || a.language}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {fmtDate(a.created_at)}
                </span>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${statusCls(
                    a.status
                  )}`}
                >
                  {a.status}
                </span>
                <button
                  data-testid={`admin-app-delete-${a.id}`}
                  aria-label="Delete application"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        `Delete the application from ${a.stage_name}? This cannot be undone.`
                      )
                    ) {
                      try {
                        await apiFetch(`/admin/applications/${a.id}`, { method: "DELETE" });
                        toast.success("Application deleted");
                        load();
                      } catch (err) {
                        toast.error(err.message);
                      }
                    }
                  }}
                  className="p-1.5 text-muted-foreground/60 hover:text-primary transition-colors duration-300"
                >
                  <Trash2 size={15} />
                </button>
                <ArrowUpRight
                  size={16}
                  className="text-muted-foreground group-hover:text-primary transition-colors"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
